import { useState } from "react";
import { Mail, Send, RefreshCw, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import api from "../../lib/api";
import toast from "react-hot-toast";

export default function EmailVerificationBanner() {
  const { user, fetchMe } = useAuthStore();
  const [isResending, setIsResending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // If user is verified or not logged in, do not render banner
  if (!user || user.emailVerified) {
    return null;
  }

  const handleResend = async () => {
    if (!user.email || isResending || cooldown > 0) return;
    setIsResending(true);
    try {
      await api.post("/api/v2/auth/resend-verification", { email: user.email });
      toast.success("Verification link sent! Please check your inbox.");
      setCooldown(60);
      const timer = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to resend verification email");
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckStatus = async () => {
    setIsChecking(true);
    try {
      await fetchMe();
      const updatedUser = useAuthStore.getState().user;
      if (updatedUser?.emailVerified) {
        toast.success("Email verified successfully! Welcome aboard.");
      } else {
        toast("Email is not verified yet. Please click the link sent to your inbox.", {
          icon: <Mail className="h-4 w-4 text-amber-500" />,
        });
      }
    } catch (error) {
      toast.error("Could not sync verification status.");
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 border-b border-amber-300/60 px-4 py-2.5 sm:px-6">
      <div className="mx-auto max-w-7xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2.5 text-amber-950 font-medium min-w-0">
          <div className="p-1.5 rounded-lg bg-amber-500/20 text-amber-800 shrink-0">
            <ShieldAlert className="h-4 w-4" />
          </div>
          <p className="truncate">
            <span className="font-bold text-amber-900">Your account email ({user.email}) is unverified.</span>{" "}
            <span className="hidden md:inline text-amber-800/90">
              Please verify your email to ensure uninterrupted access to campaigns and notifications.
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-end">
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending || cooldown > 0}
            className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold transition-all shadow-xs flex items-center justify-center gap-1.5 disabled:opacity-50 text-[11px]"
          >
            {isResending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
            <span>{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Link"}</span>
          </button>

          <button
            type="button"
            onClick={handleCheckStatus}
            disabled={isChecking}
            className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg bg-white hover:bg-amber-100/60 border border-amber-300 text-amber-950 font-bold transition-all shadow-xs flex items-center justify-center gap-1.5 disabled:opacity-50 text-[11px]"
          >
            {isChecking ? (
              <Loader2 className="h-3 w-3 animate-spin text-amber-800" />
            ) : (
              <RefreshCw className="h-3 w-3 text-amber-800" />
            )}
            <span>I've Verified</span>
          </button>
        </div>
      </div>
    </div>
  );
}
